import { PasswordRequirements } from '@/components/auth/PasswordRequirements';
import { Field } from '@/components/ui/field';
import { toaster } from '@/components/ui/toaster';
import { useAcceptBrandSignupInvitationAction } from '@/hooks/actions/use-accept-brand-signup-invitation-action';
import { useRequestBrandSignupCodeAction } from '@/hooks/actions/use-request-brand-signup-code-action';
import { metaPixel } from '@/libs/meta-pixel';
import { isWorkEmailRequiredError, type AuthError } from '@/pages/auth/_components/auth-error';
import { AuthErrorAlert } from '@/pages/auth/_components/AuthErrorAlert';
import { authFieldControlProps, authFieldLabelSx } from '@/pages/auth/_components/auth-field-label';
import { Button, Fieldset, HStack, Input, Stack, Text, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useId, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { signupAuthError } from './signup-errors';
import { signupLanguage, type SignupDetails } from './signup-details';

interface SignupFormProps {
  error: AuthError | null;
  onError: (error: AuthError | null) => void;
  /** The invited brand's account is created: it goes on. */
  onSuccess: () => void;
  /**
   * The brand signing up on its own was sent a code: the account is created
   * once it gives it back.
   */
  onCodeSent?: (details: SignupDetails) => void;
  /** Accepts this admin invitation; without one the brand signs up on its own. */
  invitationToken?: string;
  /** Values to start from, when the brand comes back to correct its address. */
  initialValues?: SignupDetails;
}

export const SignupForm: React.FC<SignupFormProps> = ({
  error,
  onError,
  onSuccess,
  onCodeSent,
  invitationToken,
  initialValues,
}) => {
  const { t, i18n } = useTranslation();
  const acceptInvitationMutation = useAcceptBrandSignupInvitationAction();
  const requestCodeMutation = useRequestBrandSignupCodeAction();
  const emailInput = useRef<HTMLInputElement | null>(null);
  const toastId = useId();
  const workEmailError = isWorkEmailRequiredError(error?.code) ? error : null;
  const workEmailMessage = workEmailError?.message;

  useEffect(() => {
    if (!workEmailMessage) return;

    toaster.error({
      id: toastId,
      title: t('signup.workEmailRequiredTitle'),
      description: workEmailMessage,
      closable: true,
    });
    // A returned Google refusal has no address to correct yet. Focus only a
    // submitted form, so mobile users can see its invalid field and fix it.
    if (workEmailError?.code !== 'work_email_required') {
      emailInput.current?.focus({ preventScroll: true });
      emailInput.current?.scrollIntoView({ block: 'center' });
    }
    return () => toaster.remove(toastId);
  }, [workEmailError, workEmailMessage, toastId, t]);

  const signupSchema = z.object({
    email: z
      .string()
      .min(1, t('signup.validation.emailRequired'))
      .email(t('signup.validation.emailInvalid')),
    firstName: z
      .string()
      .min(1, t('signup.validation.firstNameRequired'))
      .min(2, t('signup.validation.firstNameTooShort')),
    lastName: z
      .string()
      .min(1, t('signup.validation.lastNameRequired'))
      .min(2, t('signup.validation.lastNameTooShort')),
    password: z
      .string()
      .min(8, t('signup.validation.passwordTooShort'))
      .regex(/[a-zA-Z]/, t('signup.validation.passwordNeedsLetter'))
      .regex(/[0-9]/, t('signup.validation.passwordNeedsNumber')),
  });

  type SignupFormData = z.infer<typeof signupSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    mode: 'onBlur',
    defaultValues: initialValues ?? {
      email: '',
      firstName: '',
      lastName: '',
      password: '',
    },
  });

  const password = watch('password');
  const emailRegistration = register('email', {
    onChange: () => {
      if (workEmailError) {
        toaster.remove(toastId);
        onError(null);
      }
    },
  });

  const onSubmit = async (data: SignupFormData) => {
    onError(null);

    try {
      if (invitationToken) {
        await acceptInvitationMutation.mutateAsync({ token: invitationToken, ...data });
        metaPixel.track('CompleteRegistration', { content_name: 'invitation' });
        onSuccess();
        return;
      }

      await requestCodeMutation.mutateAsync({
        email: data.email,
        language: signupLanguage(i18n.language),
      });
      onCodeSent?.(data);
    } catch (failure) {
      onError(
        signupAuthError(
          failure,
          (key, fallback) => (fallback === undefined ? t(key) : t(key, { defaultValue: fallback })),
          invitationToken ? undefined : 'signup.verify.errors.sendFailed'
        )
      );
    }
  };

  return (
    <Stack as="form" onSubmit={handleSubmit(onSubmit)} gap={4} width="full">
      {error && !workEmailError && <AuthErrorAlert error={error} />}

      <Fieldset.Root size="lg" width="100%" colorPalette="primary">
        <Fieldset.Content>
          <VStack gap={3} width="full">
            <Field
              ids={{ errorText: 'signup-email-error' }}
              invalid={!!workEmailError || !!errors.email}
              errorText={workEmailMessage ?? errors.email?.message}
              required
            >
              <label
                htmlFor="signup-email"
                style={{ ...authFieldLabelSx, display: 'block', marginBottom: '7px' }}
              >
                {t('signup.workEmailLabel')}
              </label>
              <Input
                id="signup-email"
                {...emailRegistration}
                ref={(input) => {
                  emailRegistration.ref(input);
                  emailInput.current = input;
                }}
                type="email"
                placeholder={t('signup.workEmailPlaceholder')}
                aria-describedby={
                  workEmailError || errors.email
                    ? 'signup-email-hint signup-email-error'
                    : 'signup-email-hint'
                }
                variant="outline"
                {...authFieldControlProps}
                autoComplete="email"
                size="lg"
              />
              <Text id="signup-email-hint" fontSize="xs" color="fg.muted">
                {t('signup.workEmailHint')}
              </Text>
            </Field>

            <HStack gap={2} width="full" align="flex-start">
              <Field errorText={errors.firstName?.message} required>
                <label
                  htmlFor="signup-firstName"
                  style={{ ...authFieldLabelSx, display: 'block', marginBottom: '7px' }}
                >
                  {t('team.join.firstNameLabel')}
                </label>
                <Input
                  id="signup-firstName"
                  {...register('firstName')}
                  type="text"
                  placeholder={t('signup.firstNamePlaceholder')}
                  variant="outline"
                  {...authFieldControlProps}
                  size="lg"
                  autoComplete="given-name"
                />
              </Field>

              <Field errorText={errors.lastName?.message} required>
                <label
                  htmlFor="signup-lastName"
                  style={{ ...authFieldLabelSx, display: 'block', marginBottom: '7px' }}
                >
                  {t('team.join.lastNameLabel')}
                </label>
                <Input
                  id="signup-lastName"
                  {...register('lastName')}
                  type="text"
                  placeholder={t('signup.lastNamePlaceholder')}
                  variant="outline"
                  {...authFieldControlProps}
                  size="lg"
                  autoComplete="family-name"
                />
              </Field>
            </HStack>

            <Field errorText={errors.password?.message} required>
              <label
                htmlFor="signup-password"
                style={{ ...authFieldLabelSx, display: 'block', marginBottom: '7px' }}
              >
                {t('authPage.passwordLabel')}
              </label>
              <Input
                id="signup-password"
                {...register('password')}
                type="password"
                placeholder={t('signup.passwordPlaceholder')}
                variant="outline"
                {...authFieldControlProps}
                size="lg"
                autoComplete="new-password"
              />
            </Field>

            <PasswordRequirements password={password || ''} />
          </VStack>
        </Fieldset.Content>

        <Button
          type="submit"
          colorPalette="primary"
          size="lg"
          width="full"
          borderRadius="12px"
          loading={
            isSubmitting || acceptInvitationMutation.isPending || requestCodeMutation.isPending
          }
          loadingText={t('signup.creatingAccount')}
          h={{ base: '44px', md: '48px' }}
          bg="primary.900"
          fontSize={{ base: '14px', md: '15px' }}
          _hover={{ bg: 'primary.800' }}
          mt={{ base: 4, md: 5 }}
        >
          {t('signup.createAccount')}
        </Button>
      </Fieldset.Root>
    </Stack>
  );
};
